import moment from 'moment-timezone';
import qs from 'query-string';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { Link } from '@sbu/components.link';

import {
  deserializeQuery,
  serializeQuery,
} from 'shared/components/internal/Filter/serializer';
import { ListItem } from 'shared/components/layout/List';

import { ContextActionProps } from './ContextAction';
import css from './ContextAction.module.scss';

interface ContextActionRecordSearchItemProps {
  contentValue: string;
  contextData?: ContextActionProps['contextData'];
  eventTimestamp?: ContextActionProps['eventTimestamp'];
  onClick?: React.MouseEventHandler;
  value: ContextActionProps['value'];
}

type Filter =
  | {
      contentValue: ContextActionRecordSearchItemProps['contentValue'];
      negated: false;
      operator: 'equals';
      prop: string;
    }
  | {
      negated: false;
      operator: 'keyword';
      value: string;
    };

const RECORD_SEARCH_PATH = '/sec/records';

const useQuery = (
  prop: string | undefined,
  newFilter: Filter,
  contentValue: ContextActionRecordSearchItemProps['contentValue']
) => {
  const location = useLocation();
  return useMemo(() => {
    if (location.pathname !== RECORD_SEARCH_PATH) {
      return serializeQuery([newFilter]);
    }

    const { q } = qs.parse(location.search);
    const filters = deserializeQuery(q as string);

    // Handle property search.
    const propertyFilter =
      prop && q?.includes(`${prop}:`)
        ? filters.map((f: any) => (f.prop === prop ? newFilter : f))
        : [...filters, newFilter];

    // Handle keyword search.
    const keywordFilter =
      !propertyFilter &&
      filters.some((f) => f.operator === 'keyword' && f.value === contentValue)
        ? filters
        : [...filters, newFilter];

    const newFilters = propertyFilter || keywordFilter;

    return serializeQuery(newFilters);
  }, [prop, newFilter, contentValue, location.search, location.pathname]);
};

const useDates = (eventTimestamp: Date | undefined) => {
  const [timestamp, setTimestamp] = useState(eventTimestamp);

  useEffect(() => {
    if (eventTimestamp) {
      return;
    }

    const intervalId = setInterval(() => {
      setTimestamp(moment().subtract(12, 'hours').toDate());
    }, 10 * 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [eventTimestamp]);

  return useMemo(
    () => [
      moment(timestamp).subtract(12, 'hours').toISOString(),
      moment(timestamp).add(12, 'hours').toISOString(),
    ],
    [timestamp]
  );
};

export const ContextActionRecordSearchItem = ({
  contentValue,
  contextData,
  eventTimestamp,
  onClick,
  value,
}: ContextActionRecordSearchItemProps) => {
  const prop =
    contextData &&
    Object.keys(contextData).find((key) => contextData[key] === value);

  const newFilter = prop
    ? ({
        contentValue,
        negated: false,
        operator: 'equals',
        prop,
      } as const)
    : ({
        negated: false,
        operator: 'keyword',
        value: `"${contentValue}"`,
      } as const);

  const query = useQuery(prop, newFilter, contentValue);
  const [startDate, endDate] = useDates(eventTimestamp);
  const href = `${RECORD_SEARCH_PATH}?${qs.stringify({
    q: query,
    startDate,
    endDate,
  })}`;

  return (
    <ListItem className={css.listItem}>
      <Link
        href={href}
        rel='noopener noreferrer'
        target='_blank'
        onClick={onClick}
      >
        Search Records for {contentValue}
      </Link>
    </ListItem>
  );
};
